import React from 'react';
import SpotlightTutorial from './SpotlightTutorial';
import { useTutorial } from '../hooks/useTutorial';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Interactive Comprehensive Tutorial for Private Landlords
 * Navigates through ALL sidebar pages and highlights every key feature
 *
 * Props:
 *  - forceShow: boolean – when true, show regardless of Firestore state
 *  - onForceComplete: () => void – called when a force-shown tutorial ends
 */
export default function InteractiveComprehensiveLandlordTutorial({ forceShow = false, onForceComplete }) {
  const { showTutorial, markTutorialCompleted } = useTutorial('landlordFirstTime');
  const navigate = useNavigate();
  const location = useLocation();

  const isVisible = forceShow || showTutorial;

  const goTo = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  const handleComplete = () => {
    markTutorialCompleted();
    if (forceShow && onForceComplete) {
      onForceComplete();
    }
  };

  const allSteps = [
    // ─── WELCOME ───
    {
      title: 'Welkom bij StudyStay Verhuurder! 🏢',
      description: 'We leiden je stap voor stap door alle functies van je verhuurderspaneel. Je leert hoe je panden beheert, huurders vindt, contracten opmaakt en meer. Laten we beginnen!',
      icon: 'building.2.fill',
      position: 'center',
      action: () => goTo('/landlord'),
      forceAction: true,
    },

    // ─── DASHBOARD ───
    {
      title: 'Dashboard 📊',
      description: 'Dit is je overzichtspagina. Je ziet hier in één oogopslag je actieve panden, ongelezen berichten, openstaande bezichtigingen en actieve contracten. Rode bolletjes tonen waar actie nodig is.',
      icon: 'house.fill',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-dashboard"]',
      action: () => goTo('/landlord'),
      forceAction: true,
    },

    // ─── PANDEN ───
    {
      title: 'Panden 🏠',
      description: 'Hier beheer je al je vastgoed. Je kunt nieuwe panden toevoegen, bestaande bewerken, foto\'s uploaden en de zichtbaarheid instellen. Elk pand heeft zijn eigen detailpagina met statistieken.',
      icon: 'house.fill',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-panden"]',
      action: () => goTo('/landlord/properties'),
      forceAction: true,
    },

    // ─── BERICHTEN ───
    {
      title: 'Berichten 💬',
      description: 'Alle communicatie met potentiële en huidige huurders vindt hier plaats. Huurders starten een chat vanuit je pandpagina. Een rood bolletje toont ongelezen berichten.',
      icon: 'message-circle',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-berichten"]',
      action: () => goTo('/landlord/messages'),
      forceAction: true,
    },

    // ─── CONTRACTEN ───
    {
      title: 'Contracten 📄',
      description: 'Beheer al je huurcontracten. Maak nieuwe contracten aan, bekijk de status (concept, getekend door huurder, actief) en volg betalingen op. Een rood bolletje verschijnt als een huurder heeft getekend en jij nog niet.',
      icon: 'file-text',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-contracten"]',
      action: () => goTo('/landlord/contracts'),
      forceAction: true,
    },

    // ─── VERHUURDE PANDEN ───
    {
      title: 'Verhuurde Panden 🔑',
      description: 'Overzicht van al je momenteel verhuurde panden. Bekijk per pand de actieve huurder, het lopende contract, betalingsstatus en communicatie. Dit is je portaal voor actief verhuurbeheer.',
      icon: 'key.fill',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-verhuurde-panden"]',
      action: () => goTo('/landlord/verhuurde-panden'),
      forceAction: true,
    },

    // ─── BEZICHTIGINGEN ───
    {
      title: 'Bezichtigingen 👁',
      description: 'Hier zie je alle bezichtigingsaanvragen van geïnteresseerde huurders. Je kunt aanvragen accepteren of afwijzen, en je ziet wie er een motivatiebrief heeft ingediend. Een rood bolletje toont openstaande aanvragen.',
      icon: 'calendar',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-bezichtigingen"]',
      action: () => goTo('/landlord/viewings'),
      forceAction: true,
    },

    // ─── AGENDA ───
    {
      title: 'Agenda 📅',
      description: 'Je agenda toont al je geplande bezichtigingen, contractvervaldagen en andere belangrijke data in een overzichtelijk kalenderformaat. Zo mis je nooit een afspraak.',
      icon: 'calendar',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-agenda"]',
      action: () => goTo('/landlord/agenda'),
      forceAction: true,
    },

    // ─── CRM KOPPELING ───
    {
      title: 'CRM Koppeling 🚀',
      description: 'Koppel je externe CRM-systeem (zoals Immovlan, Zimmo, etc.) aan StudyStay. Zo worden je panden automatisch gesynchroniseerd en bespaar je dubbel werk.',
      icon: 'plus.circle.fill',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-crm"]',
      action: () => goTo('/landlord/crm'),
      forceAction: true,
    },

    // ─── PROFIEL ───
    {
      title: 'Profiel 👤',
      description: 'Bewerk je persoonlijke en bedrijfsgegevens. Voeg een profielfoto toe, contactgegevens en een beschrijving. Een compleet profiel wekt meer vertrouwen bij huurders.',
      icon: 'person.crop.circle.fill',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-profiel"]',
      action: () => goTo('/landlord/profile'),
      forceAction: true,
    },

    // ─── ABONNEMENT ───
    {
      title: 'Abonnement 💳',
      description: 'Beheer je StudyStay-abonnement. Bekijk je huidige plan, upgrade of downgrade, en bekijk je facturatiegeschiedenis.',
      icon: 'checkmark.circle.fill',
      position: 'right',
      targetSelector: '[data-tutorial="landlord-abonnement"]',
      action: () => goTo('/landlord/billing'),
      forceAction: true,
    },

    // ─── DONE ───
    {
      title: 'Je bent klaar! 🎉',
      description: 'Je kent nu alle functies van je verhuurderspaneel. Voeg je eerste pand toe en begin met het vinden van de ideale huurder! Tip: rode bolletjes in de sidebar tonen waar actie nodig is.',
      icon: 'checkmark.circle.fill',
      position: 'center',
      action: () => goTo('/landlord'),
    },
  ];

  if (!isVisible) return null;

  return (
    <SpotlightTutorial
      steps={allSteps}
      isPresented={isVisible}
      onComplete={handleComplete}
    />
  );
}
